import _ from 'lodash';

import {
  BACKGROUND,
  BODY_CONTENT,
  CONTENT,
  FONT,
  FONTS_WITH_TINYMCE_GOOGLE_FONTS,
  FOOTER,
  GOOGLE_FONTS,
  HEADER
} from '../constants';
import type { IPublicationNavMenuStory, IStory, IStoryContent, IStoryFolder } from '../types';

interface StoryContentFilterOptions {
  readonly hideFooter?: boolean;
  readonly hideHeader?: boolean;
}

interface IdentifiedItem {
  readonly _id: string;
}

interface RuntimeIdentifiedCollection {
  concat(other: unknown): unknown;
  filter(predicate: (item: IdentifiedItem) => boolean): RuntimeIdentifiedCollection;
  find(predicate: (item: IdentifiedItem) => boolean): IdentifiedItem | undefined;
}

interface MapLike<K, V> {
  get(key: K): V | undefined;
  keys(): IterableIterator<K>;
}

interface FontFamilies {
  readonly headline: string;
  readonly paragraph: string;
}

// Sorts the storyContents groups by type in this order: header, body_content, footer
export function sortStoryContents(storyContents: IStoryContent[]): IStoryContent[] {
  const headerStoryContents = storyContents.filter((storyContent) => storyContent.type === HEADER);
  // Sort the content group by order prop
  const contentStoryContents = _.sortBy(
    storyContents.filter((storyContent) => storyContent.type === BODY_CONTENT || storyContent.type === CONTENT),
    (storyContent) => storyContent.order
  );

  const footerStoryContents = storyContents.filter((storyContent) => storyContent.type === FOOTER);

  return [...headerStoryContents, ...contentStoryContents, ...footerStoryContents];
}

// Updates the order of each of the storyContents based on their index
export function orderStoryContents(storyContents: IStoryContent[]): IStoryContent[] {
  return storyContents.map((storyContent, idx) => ({
    ...storyContent,
    // tslint:disable-next-line restrict-plus-operands
    order: idx + 1
  }));
}

// Sorts the menu stories based on sorting order or title
export const sortMenuStories = (navMenuStories: IPublicationNavMenuStory[]) => {
  return navMenuStories.sort((navMenuStory1, navMenuStory2) => {
    if (navMenuStory1.order === 0 && navMenuStory2.order === 0) {
      return navMenuStory1.story.title > navMenuStory2.story.title ? 1 : -1;
    } else {
      return navMenuStory1.order > navMenuStory2.order ? 1 : -1;
    }
  });
};

export function sortAndOrderStoryContents(storyContents: IStoryContent[]): IStoryContent[] {
  return orderStoryContents(sortStoryContents(storyContents));
}

export function filterAndOrderStoryContents(
  storyContents: IStoryContent[],
  filterOptions: StoryContentFilterOptions
): IStoryContent[] {
  for (let index = 0; index < storyContents.length; index++) {
    if (storyContents[index].type === HEADER && filterOptions.hideHeader) {
      storyContents[index].active = false;
    }
    if (storyContents[index].type === FOOTER && filterOptions.hideFooter) {
      storyContents[index].active = false;
    }
  }
  return sortAndOrderStoryContents(storyContents);
}

// Temporary fix until DB migration
export function initializeCustomColors(story: Pick<IStory, 'meta'>) {
  if (story.meta && story.meta.customColors) {
    const headerCustomColor = story.meta.customColors[HEADER];
    const contentBodyCustomColor = story.meta.customColors[BODY_CONTENT] || story.meta.customColors[CONTENT];
    const footerCustomColor = story.meta.customColors[FOOTER];
    //header, footer, and body_content are expected to be of type object
    story.meta.customColors = {
      header:
        headerCustomColor && !Array.isArray(headerCustomColor)
          ? headerCustomColor
          : {
              [BACKGROUND]: headerCustomColor || [],
              [FONT]: []
            },
      body_content:
        contentBodyCustomColor && !Array.isArray(contentBodyCustomColor)
          ? contentBodyCustomColor
          : {
              [BACKGROUND]: contentBodyCustomColor || [],
              [FONT]: []
            },
      footer:
        footerCustomColor && !Array.isArray(footerCustomColor)
          ? footerCustomColor
          : {
              [BACKGROUND]: footerCustomColor || [],
              [FONT]: []
            }
    };
  } else {
    story.meta = {
      customColors: {
        header: { [BACKGROUND]: [], [FONT]: [] },
        body_content: { [BACKGROUND]: [], [FONT]: [] },
        footer: { [BACKGROUND]: [], [FONT]: [] }
      }
    };
  }
  return story.meta.customColors;
}

export function generatePageTitle(title?: string | null, name?: string | null): string {
  if (title && name) {
    return `${name} Stories - ${title}`;
  } else if (title) {
    return `OpenGov Stories - ${title}`;
  } else if (name) {
    return `${name} Stories`;
  }
  return 'OpenGov Stories';
}

export function b64EncodeUnicode(str: string): string {
  // Check if the method is called from a node environment
  if (process.versions && process.versions.node) {
    return Buffer.from(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
        return String.fromCharCode(Number(`0x${p1}`));
      }),
      'ascii'
    ).toString('base64');
  }
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode(Number(`0x${p1}`));
    })
  );
}

export function arrayToList(array?: readonly string[]): string {
  if (!array) return '';
  return array.join(', ').replace(/, ((?:.(?!, ))+)$/, ' and $1');
}

// Had to adapt this from compose-app/immutable since we use _id instead of id 🙄
export function mergeListsById<T extends IdentifiedItem>(a: T[], b: T[]): T[];
export function mergeListsById<A, B>(a: A, b: B): A;
export function mergeListsById(a: unknown, b: unknown): unknown {
  const currentItems = a as RuntimeIdentifiedCollection;
  const updatedItems = b as RuntimeIdentifiedCollection;
  return currentItems.filter(ar => !updatedItems.find(br => br._id === ar._id)).concat(b);
}

// Take a string input, make sure it's a valid hex color code
export function validateHexString(hexString: string): RegExpMatchArray | null {
  return hexString.match(/\b[0-9A-Fa-f]{6}\b/g);
}

export function createFontFamilyString(fontObj: FontFamilies): string {
  const fontArray = [fontObj.headline, fontObj.paragraph].map(fontFamily => {
    if (GOOGLE_FONTS.indexOf(fontFamily) === -1) return '';

    if (fontFamily) {
      const array = fontFamily.split('-');
      const upperCaseArray = array.map((item) => {
        return `${item.charAt(0).toUpperCase()}${item.slice(1)}`;
      });
      return upperCaseArray.join('+');
    }
  });

  return fontArray.filter((item) => item).join('|');
}

export function mapFontFamilyStrings(fontObj: FontFamilies): FontFamilies {
  const newFontObj = {
    headline: '',
    paragraph: ''
  };

  FONTS_WITH_TINYMCE_GOOGLE_FONTS.forEach((font) => {
    if (fontObj.headline === font.id) {
      newFontObj.headline = font.name;
    }
    if (fontObj.paragraph === font.id) {
      newFontObj.paragraph = font.name;
    }
  });

  return newFontObj;
}

export const gql = (strings: TemplateStringsArray | string, ...values: unknown[]): string => {
  if (typeof strings === 'string') {
    return [strings, ...values].join('');
  }

  return strings.reduce((query, segment, index) => `${query}${segment}${values[index] ?? ''}`, '');
};

export const getMappedKeyByValue = <K, V>(map: MapLike<K, V>, searchValue: V): K | undefined =>
  [...map.keys()].find(key => map.get(key) === searchValue);

export const buildPublicationFolderHierarchyMap = (folderList: IStoryFolder[]): Record<string, IStoryFolder> => {
  const publicationFolderMap: Record<string, IStoryFolder> = {};
  for (let i = 0; i < folderList.length; i++) {
    const folder = folderList[i];
    let subFolders = folderList.filter((subFolder) => folder.id === subFolder.parentFolderId);
    subFolders = _.sortBy(subFolders, ['order', 'title']);
    publicationFolderMap[folder.id] = Object.assign(folder, { subFolders: subFolders });
  }
  return publicationFolderMap;
};
